import type React from 'react'
import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import styled, { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { GarminCallback } from './components/auth/GarminCallback'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { Chat } from './components/chat/Chat'
import { InviteAthleteModal } from './components/coach/InviteAthleteModal'
import { AnalysisView } from './components/layout/AnalysisView'
import { AppHeader } from './components/layout/AppHeader'
import { AthleteDetails } from './components/layout/AthleteDetails'
import { AthletesOverview } from './components/layout/AthletesOverview'
import { AutomationView } from './components/layout/AutomationView'
import { CoachOnboardingTour } from './components/onboarding/CoachOnboardingTour'
import { EditThresholdView } from './components/views/EditThresholdView'
import { ThresholdManagementView } from './components/views/ThresholdManagementView'
import { WorkoutAnalysisView } from './components/views/WorkoutAnalysisView'
import { AthletesProvider, useAthletes } from './contexts/AthletesContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ChatProvider } from './contexts/ChatContext'
import { CoachOnboardingProvider } from './contexts/CoachOnboardingContext'
import { RoleProvider, useRole } from './contexts/RoleContext'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { useWorkouts } from './hooks/useWorkouts'
import AthleteSignupPage from './pages/AthleteSignupPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import { GlobalStyles } from './theme/GlobalStyles'

const AppContainer = styled.div`
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
`

const MainContent = styled.main`
  flex: 1;
  margin-top: 50px;
  overflow: hidden;

  @media (max-width: 640px) {
    margin-top: 40px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

// Route component that redirects based on user role
const DashboardRoute: React.FC = () => {
  const { currentRole, loading } = useRole()

  if (loading || !currentRole) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    )
  }

  // Redirect based on current role
  if (currentRole === 'athlete') {
    return <Navigate to="/dashboard" replace />
  } else if (currentRole === 'coach') {
    return <Navigate to="/athletes" replace />
  }

  // current role is not (yet) set
  return (
    <LoadingContainer>
      <LoadingSpinner />
    </LoadingContainer>
  )
}

// Layout component that includes header
const AppLayout: React.FC = () => {
  return (
    <AppContainer>
      <AppHeader />
      <MainContent>
        <Routes>
          <Route path="/" element={<DashboardRoute />} />
          <Route path="/dashboard" element={<PersonalDashboardRoute />} />
          <Route path="/athletes" element={<AthletesOverviewRoute />} />
          <Route path="/athletes/:id" element={<AthleteDetailRoute />} />
          <Route path="/athletes/:id/thresholds" element={<AthleteThresholdsRoute />} />
          <Route path="/athletes/:id/thresholds/:thresholdId" element={<EditThresholdView />} />
          <Route path="/athletes/:id/workouts/:workoutId" element={<AthleteWorkoutRoute />} />
          <Route path="/analysis" element={<AnalysisView />} />
          <Route path="/automations" element={<AutomationView />} />
          <Route path="/automations/:automationId" element={<AutomationView />} />
        </Routes>
      </MainContent>
      <Chat />
      <CoachOnboardingTour />
    </AppContainer>
  )
}

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
`

const EmptyStatePanel = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  padding: 3rem;
  max-width: 500px;
  text-align: center;
`

const EmptyStateTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`

const InviteButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverted};
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

const AddSelfLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.md};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

// Route component for Athletes Overview
const AthletesOverviewRoute: React.FC = () => {
  const navigate = useNavigate()
  const { session } = useAuth()
  const { athletes, loading, error, addSelfAsAthlete } = useAthletes()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [addingSelf, setAddingSelf] = useState(false)

  const handleInviteClick = () => {
    setShowInviteModal(true)
  }

  const handleAddSelfAsAthlete = async () => {
    setAddingSelf(true)
    const result = await addSelfAsAthlete()
    setAddingSelf(false)

    if (!result.success && result.error) {
      alert(result.error)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading athletes...</div>
  }

  if (error) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Error: {error}</div>
  }

  if (athletes.length === 0) {
    return (
      <>
        <EmptyStateContainer>
          <EmptyStatePanel>
            <EmptyStateTitle>No Athletes Connected</EmptyStateTitle>
            <EmptyStateText>
              You don't have any athletes connected yet. Invite some to start coaching or test with yourself.
            </EmptyStateText>
            <InviteButton data-tour="invite-button" onClick={handleInviteClick}>
              Invite Athlete
            </InviteButton>
            <AddSelfLink data-tour="add-self-link" onClick={handleAddSelfAsAthlete} disabled={addingSelf}>
              {addingSelf ? 'Adding...' : 'Or add yourself as an athlete'}
            </AddSelfLink>
          </EmptyStatePanel>
        </EmptyStateContainer>
        <InviteAthleteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          coachId={session?.user?.id || ''}
          coachName={
            `${session?.user?.user_metadata?.first_name || ''} ${session?.user?.user_metadata?.last_name || ''}`.trim() ||
            session?.user?.email ||
            'Coach'
          }
        />
      </>
    )
  }

  return (
    <>
      <AthletesOverview
        athletes={athletes}
        onAthleteClick={(athleteId: string) => {
          // Use View Transition API for smooth animations
          if (document.startViewTransition) {
            document.startViewTransition(() => {
              flushSync(() => {
                navigate(`/athletes/${athleteId}`)
              })
            })
          } else {
            console.log('View Transition API not supported')
            navigate(`/athletes/${athleteId}`)
          }
        }}
        onAnalyze={(athleteId: string, athleteName: string) => {
          navigate(`/analysis?athleteId=${athleteId}&athleteName=${encodeURIComponent(athleteName)}`)
        }}
      />
      <InviteAthleteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        coachId={session?.user?.id || ''}
        coachName={
          `${session?.user?.user_metadata?.first_name || ''} ${session?.user?.user_metadata?.last_name || ''}`.trim() ||
          session?.user?.email ||
          'Coach'
        }
      />
    </>
  )
}

// Route component for personal dashboard (no ID needed)
const PersonalDashboardRoute: React.FC = () => {
  return <AthleteDetails />
}

// Route component for Athlete Detail (requires ID parameter)
const AthleteDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>()

  if (!id) {
    return <Navigate to="/athletes" replace />
  }

  return <AthleteDetails athleteId={id} />
}

// Route component for Athlete Thresholds (requires ID parameter)
const AthleteThresholdsRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  if (!id) {
    return <Navigate to="/athletes" replace />
  }

  return <ThresholdManagementView athleteId={id} onBack={() => navigate(`/athletes/`)} />
}

// Route component for Athlete Workout Analysis (requires ID and workoutId parameters)
const AthleteWorkoutRoute: React.FC = () => {
  const { id, workoutId } = useParams<{ id: string; workoutId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { loadWorkoutById } = useWorkouts(id)
  const [workout, setWorkout] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadWorkout = async () => {
      if (!workoutId) {
        setError('No workout ID provided')
        setLoading(false)
        return
      }

      try {
        const workoutData = await loadWorkoutById(workoutId)
        setWorkout(workoutData)
      } catch (err) {
        console.error('Error loading workout:', err)
        setError(err instanceof Error ? err.message : 'Failed to load workout')
      } finally {
        setLoading(false)
      }
    }

    loadWorkout()
  }, [workoutId, loadWorkoutById])

  if (!id || !workoutId) {
    return <Navigate to="/athletes" replace />
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading workout...</div>
  }

  if (error || !workout) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Error: {error || 'Workout not found'}</div>
    )
  }

  // Preserve week parameter when navigating back
  const weekParam = searchParams.get('week')
  const backUrl = weekParam ? `/athletes/${id}?week=${weekParam}` : `/athletes/${id}`

  return <WorkoutAnalysisView workout={workout} onBack={() => navigate(backUrl)} />
}

// Inner component to access auth session
const AuthenticatedApp: React.FC = () => {
  const { session } = useAuth()

  return (
    <ChatProvider userId={session?.user?.id}>
      <RoleProvider>
        <AthletesProvider>
          <CoachOnboardingProvider>
            <Router>
              <Routes>
                <Route path="/garmin-callback" element={<GarminCallback />} />
                <Route path="/signup/athlete" element={<AthleteSignupPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </CoachOnboardingProvider>
        </AthletesProvider>
      </RoleProvider>
    </ChatProvider>
  )
}

// Wrapper component to access theme
const ThemedApp: React.FC = () => {
  const { theme } = useTheme()

  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyles />
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </StyledThemeProvider>
  )
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  )
}

export default App
